import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';

/** Pick the server's user-facing detail out of an envelope. Most handlers
 *  put it at the top level (`message`); a few (canary gate, kb_not_granted,
 *  kb_agent_id_required) nest it under `body.message` instead — and some of
 *  those also leave a non-informative placeholder ("error") at the top
 *  level, so the nested detail must win whenever it's present rather than
 *  only being a fallback for when the top level is empty. renderer.validate()
 *  failures (draft approve/publish, 422) carry no `body.message` at all —
 *  just a `body.errors` list of `{field, code, message}` — so those are
 *  joined into one string and given the same precedence as `body.message`,
 *  ahead of the generic top-level "error" placeholder. */
function envelopeMessage(
  error: { message?: string; body?: { message?: string; errors?: Array<{ message?: string }> } } | undefined,
): string {
  const nested = typeof error?.body?.message === 'string' ? error.body.message.trim() : '';
  const fieldErrors = Array.isArray(error?.body?.errors)
    ? error.body.errors
        .map((e) => (typeof e?.message === 'string' ? e.message.trim() : ''))
        .filter((m) => m.length > 0)
    : [];
  const top = typeof error?.message === 'string' ? error.message.trim() : '';
  return nested || (fieldErrors.length ? fieldErrors.join(' ') : '') || top;
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private auth: AuthService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: { status?: number; error?: { message?: string; statusCode?: string; body?: { message?: string; errors?: Array<{ message?: string }> } }; statusText?: string }) => {
        if (err.status === 401) {
          // logout() does a full-page redirect (and self-guards against a
          // concurrent-401 burst), so the old router.navigate here was dead
          // code — the page is already leaving.
          this.auth.logout();
          return throwError(() => ({ message: 'Session expired. Please access via WG Agents portal.', status: 401 }));
        }
        if (err.status === 403) {
          const isTokenExpired =
            err.error?.statusCode === 'unauthorized' ||
            this.auth.isBackendTokenExpired();
          if (isTokenExpired) {
            this.auth.logout();
            return throwError(() => ({ message: 'Session expired. Please access via WG Agents portal.', status: 403 }));
          }
          // The studio-api's 403s carry actionable envelope messages — the
          // canary gate, the knowledge-base creator grant (kb_not_granted) —
          // so pass the server's message through instead of flattening every
          // 403 to the generic text. The envelope puts it at error.message
          // (same path the 404 branch reads); some handlers nest detail under
          // error.body.message. Generic text stays the fallback for bodiless
          // 403s (API Gateway/WAF denials, HTML error pages).
          const serverMessage = envelopeMessage(err.error);
          return throwError(() => ({
            message: serverMessage || 'You are not authorised to do this action.',
            status: 403,
            error: err.error,
          }));
        }
        // 404 carries a meaningful server message (e.g. "Draft <id> not
        // found") that RealAgentBuilderService.mapError turns into a
        // NotFoundError — forward the envelope + its message instead of
        // flattening it to the generic text. 409 (optimistic-lock conflict)
        // is handled the same way by the generic branch below.
        if (err.status === 404) {
          return throwError(() => ({
            message: err.error?.message || 'The requested item was not found.',
            status: err.status,
            error: err.error,
          }));
        }
        // 400s are application-level validation failures the studio-api's
        // route handlers return deliberately (e.g. "owner is required",
        // "expectedVersion is required", kb_agent_id_required) — the envelope
        // wrapper stamps the top-level message "error" for every handler-
        // returned 4xx and puts the real, safe-to-show text in body.message,
        // so forward it instead of flattening every 400 to generic text.
        if (err.status === 400) {
          const serverMessage = envelopeMessage(err.error);
          return throwError(() => ({
            message: serverMessage || 'Something went wrong. Please contact the administrator.',
            status: err.status,
            error: err.error,
          }));
        }
        // 422s are renderer.validate() failures (draft approve/publish) —
        // the envelope's top-level message is the same non-informative
        // "error" placeholder as the 400 case, and the real detail lives in
        // body.errors (no body.message at all), so forward it instead of
        // falling through to the generic branch's bare statusText.
        if (err.status === 422) {
          const serverMessage = envelopeMessage(err.error);
          return throwError(() => ({
            message: serverMessage || 'This agent is missing required information.',
            status: err.status,
            error: err.error,
          }));
        }
        // 500s are unexpected server crashes (raw exception text, stack
        // traces) — never safe to surface, so this one stays generic.
        if (err.status === 500) {
          return throwError(() => ({
            message: 'Something went wrong. Please contact the administrator.',
            status: err.status
          }));
        }
        const error = envelopeMessage(err.error) || err.statusText;
        return throwError(() => ({
          message: error,
          status: err.status,
          error: err.error
        }));
      })
    );
  }
}
